import React, { useContext, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useMap } from 'react-map-gl';
import { useLeaflet, FeatureGroup } from 'react-leaflet';
import EndpointsOverlay from '@opentripplanner/endpoints-overlay';
import MLEndpointsOverlay from '@opentripplanner/endpoints-overlay-ml';
import { BoundOptionsContext } from '#/map/providers/BoundOptionsProvider';
import { IconWithOtpUiCompatibility } from '#/shared/components/LocationTypeIcon';
import featureFlags from '#/shared/feature-flags';
import { locationType } from '#/prop-types';
import { zoomTo, fitTo } from '#/map/utils/maplibre';

const { mapLibre } = featureFlags;

function TripRequestOverlay({ toLocation, fromLocation, setEndpointLocation }) {
  const { current: mapLibreMap } = useMap();
  const { map: leafletMap } = useLeaflet();
  const map = mapLibre ? mapLibreMap : leafletMap;
  const options = useContext(BoundOptionsContext);
  const firstDefinedEndpoint = fromLocation || toLocation;

  useEffect(() => {
    // Skip if map isn't available
    if (!map) return;

    if (fromLocation && toLocation) {
      const boundsToUse = [fromLocation, toLocation];
      fitTo(map, boundsToUse, mapLibre ? {} : options);
    } else if (firstDefinedEndpoint) {
      zoomTo(map, firstDefinedEndpoint, mapLibre ? {} : options);
    }
  }, [map, options, fromLocation, toLocation, firstDefinedEndpoint]);

  // This HOC uses hooks so instantiate it outside the return statement
  // so it's not called conditionally
  const MapMarkerIcon = IconWithOtpUiCompatibility();

  return mapLibre ? (
    <MLEndpointsOverlay
      toLocation={toLocation}
      fromLocation={fromLocation}
      setLocation={setEndpointLocation}
      MapMarkerIcon={MapMarkerIcon}
    />
  ) : (
    <FeatureGroup>
      {firstDefinedEndpoint && (
        <EndpointsOverlay
          toLocation={toLocation}
          fromLocation={fromLocation}
          setLocation={setEndpointLocation}
          MapMarkerIcon={MapMarkerIcon}
        />
      )}
    </FeatureGroup>
  );
}

TripRequestOverlay.propTypes = {
  /** Trip origin */
  fromLocation: locationType,
  /** Trip destination */
  toLocation: locationType,
  /** Contains the formatted itinerary data for the transitive geometry layer */
  transitiveData: PropTypes.shape({}),
  /** Called when a trip endpoint is clicked and dragged */
  setEndpointLocation: PropTypes.func.isRequired,
};

TripRequestOverlay.defaultProps = {
  fromLocation: null,
  toLocation: null,
  transitiveData: null,
};

export default TripRequestOverlay;
